// src/features/legacy-reports/LegacyReportsUtils.ts

import { DateTime } from 'luxon';
import type { LegacyUnitOfTime } from '@/lib/constants/legacyReports';

/**
 * Formatting helpers for the Legacy Reports compatibility surface. Display
 * parity with the legacy dashboard: Views = cPlays, Viewers = cUniques,
 * watch time from cDuration (SECONDS). Deliberately local to this feature —
 * nothing is shared with features/analytics (no-blending rule, ADR-003 §9).
 */
export class LegacyReportsUtils {
  /** "4h 12m" style label from legacy cDuration / viewSeconds (seconds). */
  static formatWatchSeconds(seconds: number): string {
    if (!isFinite(seconds) || seconds <= 0) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return `${Math.floor(seconds)}s`;
  }

  static formatCount(value: number): string {
    if (!isFinite(value)) return '0';
    return Math.round(value).toLocaleString();
  }

  /**
   * Legacy timeseries label convention (lib2 StatTypes
   * getDateLabelForUnitOfTime): HOUR buckets render in local time; day and
   * larger buckets stay UTC deliberately — a UTC-midnight day bucket must not
   * display as the prior local day. `localZone` is parameterized for
   * deterministic tests only.
   */
  static timeseriesLabel(
    tsSeconds: number,
    unitOfTime: LegacyUnitOfTime,
    localZone: string = 'local'
  ): string {
    const utcDT = DateTime.fromSeconds(tsSeconds, { zone: 'utc' });
    switch (unitOfTime) {
      case 'h':
        return utcDT.setZone(localZone).toFormat('EEE ha');
      case 'd':
      case 'w':
        return utcDT.toFormat('LLL d');
      case 'm':
        return utcDT.toFormat('LLL');
      case 'y':
        return utcDT.toFormat('yyyy');
      default:
        return utcDT.toFormat('LLL d');
    }
  }
}
